import axios, { type AxiosInstance } from 'axios';
import { markSessionInvalid, useToken } from '@/composables/useToken';

let clientInstance: AxiosInstance | null = null;

export function getApiClient(): AxiosInstance {
  if (clientInstance) return clientInstance;

  const { getToken } = useToken();
  const client = axios.create({
    headers: { 'Content-Type': 'application/json' }
  });

  client.interceptors.request.use((config) => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  // The JWT we boot with is signature-valid, but the session it points at can
  // be revoked or expire in Mongo at any moment — at which point SessionAliveFilter
  // returns 401 on every subsequent BFF call. Convert the first 401 into the
  // SPA's error state so the shell renders a "session ended" message and the
  // parent gets a kaunt-error postMessage, instead of the component silently
  // looping on failed calls.
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.response?.status === 401) {
        markSessionInvalid(
          'Your session has ended. Please return to your application to start a new one.',
          'session-revoked'
        );
      }
      return Promise.reject(error);
    }
  );

  clientInstance = client;
  return client;
}

const base = '/api/documentai-feedback-agent';
const insightsBase = '/api/insights';

function paramsWithOptionalCompany(
  companyName: string | null,
  rest: Record<string, string | number | undefined>
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(rest)) {
    if (value !== undefined && value !== '') params[key] = value;
  }
  // When the session is company-scoped the BFF reads companyName from claims and
  // refuses a query value that contradicts it. We only pass it for tenant-scoped
  // sessions where the picker provides one.
  if (companyName) params.companyName = companyName;
  return params;
}

export const api = {
  insights: {
    getCompanies: () =>
      getApiClient().get(`${insightsBase}/companies`),
    getCounts: (companyName: string | null, startDate: string, endDate: string) =>
      getApiClient().get(`${insightsBase}/counts`, {
        params: paramsWithOptionalCompany(companyName, { startDate, endDate })
      }),
    getTrends: (companyName: string | null, startDate: string, endDate: string) =>
      getApiClient().get(`${insightsBase}/trends`, {
        params: paramsWithOptionalCompany(companyName, { startDate, endDate })
      }),
    getDimensions: (companyName: string | null, startDate: string, endDate: string) =>
      getApiClient().get(`${insightsBase}/dimensions`, {
        params: paramsWithOptionalCompany(companyName, { startDate, endDate })
      }),
    getPrecision: (companyName: string | null, period: string) =>
      getApiClient().get(`${insightsBase}/precision`, {
        params: paramsWithOptionalCompany(companyName, { period })
      })
  },
  ruleAssistant: {
    createSession: () =>
      getApiClient().post(`${base}/sessions`),
    getDocument: () =>
      getApiClient().get(`${base}/document`),
    getSession: (chatSessionId: string) =>
      getApiClient().get(`${base}/sessions/${chatSessionId}`),
    sendMessage: (chatSessionId: string, message: string) =>
      getApiClient().post(`${base}/sessions/${chatSessionId}/messages`, { message }),
    getSessionEvents: (chatSessionId: string, afterSeq: number, wait: number, signal?: AbortSignal) =>
      getApiClient().get(`${base}/sessions/${chatSessionId}/events`, {
        params: { afterSeq, wait },
        signal,
        timeout: (wait + 5) * 1000
      }),
    getProposal: (chatSessionId: string, proposalId: string) =>
      getApiClient().get(`${base}/sessions/${chatSessionId}/proposals/${proposalId}`),
    confirmProposal: (chatSessionId: string, proposalId: string) =>
      getApiClient().post(
        `${base}/sessions/${chatSessionId}/proposals/${proposalId}/confirm`
      ),
    discardProposal: (chatSessionId: string, proposalId: string) =>
      getApiClient().post(
        `${base}/sessions/${chatSessionId}/proposals/${proposalId}/discard`
      ),
    startPreview: (chatSessionId: string, proposalId: string) =>
      getApiClient().post(
        `${base}/sessions/${chatSessionId}/proposals/${proposalId}/preview`
      ),
    startBundlePreview: (chatSessionId: string, proposalIds: string[]) =>
      getApiClient().post(
        `${base}/sessions/${chatSessionId}/preview`,
        { proposalIds }
      ),
    getPreviewRun: (chatSessionId: string, previewRunId: string) =>
      getApiClient().get(`${base}/sessions/${chatSessionId}/preview-runs/${previewRunId}`)
  }
};
